// Per-row betting-model selection (spec 007 FE.P1, per-row variant).
//
// Each slate row independently chooses which model's betting odds it shows;
// the default is baseline. Selections persist per league in localStorage,
// keyed by game_id. Baseline is represented by the ABSENCE of an entry, so a
// row reverting to baseline drops its key (keeps the map small).

const VERSION = "v1";

function storageKey(leagueId: string): string {
  return `predabet.betting_row_sel.${leagueId}.${VERSION}`;
}

/** Load the saved {game_id -> model_id} map for a league. Tolerates missing
 *  / malformed storage by returning an empty map. */
export function loadRowSelections(leagueId: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(storageKey(leagueId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "string" && v && v !== "baseline") out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export function saveRowSelections(
  leagueId: string,
  selections: Record<string, string>,
): void {
  try {
    localStorage.setItem(storageKey(leagueId), JSON.stringify(selections));
  } catch {
    /* ignore quota / unavailable storage */
  }
}

/** Drop selections for games no longer in the slate, so the persisted map
 *  doesn't accumulate stale game ids over time. */
export function pruneRowSelections(
  selections: Record<string, string>,
  gameIds: Iterable<string>,
): Record<string, string> {
  const valid = new Set(gameIds);
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(selections)) {
    if (valid.has(k)) out[k] = v;
  }
  return out;
}
